import ImgCrop from "antd-img-crop";
import styled from "styled-components";
import { Text } from "../../../../components/Text";
import { Rounded } from "../../../../theme/Rounded";
import { Shadow } from "../../../../theme/Shadow";
import { Spacing } from "../../../../theme/Spacing";
import { Clickable } from "../../../../components/Clickable";
import { Flex } from "../../../../components/Flex";
import { Input } from "../../../../components/Input";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons/faPaperPlane";
import { faPaperclip } from "@fortawesome/free-solid-svg-icons/faPaperclip";
import { faMicrophone } from "@fortawesome/free-solid-svg-icons/faMicrophone";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { useTranslation } from "react-i18next";
import { Fragment, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { whatsappChatScrollToBottom } from "../../utils/integration-utils";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { faClipboard } from "@fortawesome/free-solid-svg-icons/faClipboard";
import { Tooltip, Upload } from "../../../../utils/local-antd";
import { Spinner } from "../../../../components/Spinner";
import { WarningModal } from "../../../../components/WarningModal";
import { TestIDs } from "../../../../tests/types/testIds";
import { MetaMessageTemplate } from "../../../../model/integration/dto/meta/MetaMessageTemplate";
import { MetaTemplateParameters } from "../../../../model/integration/dto/meta/MetaTemplateParameters";
import { useVoiceRecorder } from "./useVoiceRecorder";
import { WAVoiceLevelBars } from "./components/WAVoiceLevelBars";
import { useQuotePreview } from "./WAMessageReply";
import { WATemplateSendModal } from "./WATemplateSendModal";
import { ConversationMessage } from "../../../../model/integration/dto/meta/MetaConversationMessage";
import { WAMetaMessageDirection } from "../../../../model/integration/types/whatsapp/WAMetaMessageDirection.enum";

const StyledClickable = styled(Clickable)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border-radius: ${Rounded.circle};
  background-color: ${({ theme }) => theme.lightSub};
  color: ${({ theme }) => theme.main};
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.sub};
  }

  &:disabled {
    opacity: 0.45;
  }
`;

const SendButton = styled(StyledClickable)`
  background-color: ${({ theme }) => theme.main};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.darkMain};
  }
`;

const ChatBox = styled(Flex)`
  position: sticky;
  bottom: 0;
  width: 100%;
  align-items: center;
  gap: ${Spacing.s};
  padding: ${Spacing.s} ${Spacing.m};
  background-color: ${({ theme }) => theme.white};
  border-top: 1px solid ${({ theme }) => theme.sub};

  .atira-input-wrapper input {
    border-radius: ${Rounded.pill};
  }
`;

type Props = {
  activeChatId: string;
  replyTo?: ConversationMessage | null;
  onCancelReply?: VoidFunction;
};

const AttachUpload = styled(Upload)`
  display: flex;

  .ant-upload {
    display: flex;
  }
`;

const ReplyBar = styled(Flex)`
  position: sticky;
  bottom: 3.5rem;
  align-items: center;
  gap: ${Spacing.s};
  padding: ${Spacing.s} ${Spacing.m};
  background-color: ${({ theme }) => theme.lightSub};
  /* A left edge alone left this reading as part of the page rather than as a
     strip attached to the composer */
  border: 1px solid ${({ theme }) => theme.sub};
  border-inline-start: 4px solid ${({ theme }) => theme.main};
  border-radius: ${Rounded.md};
  margin: 0 ${Spacing.s} ${Spacing.xs};
  box-shadow: ${Shadow.SM};
`;

const ReplyQuote = styled(Flex)`
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const ReplySnippet = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  unicode-bidi: plaintext;
  text-align: start;
`;

const RecordingBar = styled(Flex)`
  flex: 1;
  align-items: center;
  gap: ${Spacing.s};
  height: 2.5rem;
  padding: 0 ${Spacing.m};
  border-radius: ${Rounded.xl};
  background-color: ${({ theme }) => theme.lightSub};
`;

const RecordingDot = styled.span`
  width: 0.6rem;
  height: 0.6rem;
  flex-shrink: 0;
  border-radius: ${Rounded.circle};
  background-color: ${({ theme }) => theme.danger};
  animation: atira-wa-recording-pulse 1.2s ease-in-out infinite;

  @keyframes atira-wa-recording-pulse {
    50% {
      opacity: 0.25;
    }
  }
`;

const formatDuration = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/** WhatsApp shows a typing indicator for 25 seconds. Re-sending a little
 * before that keeps it up for someone still writing, without a request per
 * keystroke.
 */
const TYPING_HOLD_MS = 20_000;

const MAX_ATTACHMENT_BYTES = 16 * 1024 * 1024;

/** Exactly what WhatsApp accepts, from Meta's own rejection message. Anything
 * else is refused here rather than after a wasted upload.
 */
const WHATSAPP_ACCEPTED_TYPES = [
  "audio/aac",
  "audio/mp4",
  "audio/mpeg",
  "audio/amr",
  "audio/ogg",
  "audio/opus",
  "application/vnd.ms-powerpoint",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/pdf",
  "text/plain",
  "application/vnd.ms-excel",
  "image/jpeg",
  "image/png",
  "image/webp",
  "video/mp4",
  "video/3gpp",
];

const SENDABLE_AS_IMAGE = ["image/jpeg", "image/png", "image/webp"];

/** Types WhatsApp has no slot for, but which are text underneath - the API
 * sends these as documents, keeping the file and its name exactly as they are.
 */
const TEXT_LIKE_TYPES = [
  "image/svg+xml",
  "application/json",
  "application/xml",
  "text/xml",
  "text/csv",
  "text/markdown",
  "text/html",
];

/** What the file picker offers.
 *
 * Everything, deliberately. Listing only what WhatsApp takes meant a zip could
 * not even be selected, which reads as the app being broken rather than as the
 * limit it is - isSendable then refuses it by name and says why.
 *
 * Still needed explicitly: antd-img-crop sets accept="image/*" itself when the
 * child does not, and then nothing but images can be picked.
 */
const ATTACHABLE_TYPES = "*/*";

const isSendable = (mimeType: string) => {
  const type = mimeType?.toLowerCase();

  return (
    WHATSAPP_ACCEPTED_TYPES.includes(type) ||
    TEXT_LIKE_TYPES.includes(type) ||
    Boolean(type?.startsWith("text/"))
  );
};

const mediaTypeOf = (mimeType: string) => {
  const type = mimeType?.toLowerCase();

  if (SENDABLE_AS_IMAGE.includes(type)) {
    return "image" as const;
  }

  if (mimeType?.startsWith("video/")) {
    return "video" as const;
  }

  if (mimeType?.startsWith("audio/")) {
    return "audio" as const;
  }

  return "document" as const;
};

export const WAChatbox: React.FC<Props> = ({
  activeChatId,
  replyTo,
  onCancelReply,
}) => {
  const [message, setMessage] = useState<string>("");
  // What was typed in each chat, so switching away to check something and
  // coming back does not throw a half-written message away
  const draftsRef = useRef<Record<string, string>>({});
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [templatesOpen, setTemplatesOpen] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const localIdRef = useRef(0);
  const typingSentAtRef = useRef(0);
  const previousChatRef = useRef(activeChatId);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const loggedInUser = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const whatsAppMetaIntegration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  const chat = useAppSelector((state) =>
    integrationSliceSelectors.selectWhatsAppMetaChatById(state, activeChatId),
  );
  const messagesTemplates = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaTemplates,
  );

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const quote = useQuotePreview(replyTo || undefined);

  const sendAttachment = async (file: File, voice = false) => {
    if (!activeChatId) {
      return;
    }

    if (file.size > MAX_ATTACHMENT_BYTES) {
      AtiraToast.error(t("integration.whatsapp.meta.media.tooLarge"));
      return;
    }

    // WhatsApp refuses anything outside its own list, whatever we send it as.
    // A recording is exempt: the API converts it before it gets there.
    if (!voice && !isSendable(file.type)) {
      AtiraToast.error(
        t("integration.whatsapp.meta.media.unsupported", {
          type: file.type || file.name,
        }),
      );
      return;
    }

    const type = mediaTypeOf(file.type);
    const caption = type === "audio" ? undefined : message.trim() || undefined;
    const localId = `local-${Date.now()}-${localIdRef.current++}`;

    const localPreviewUrl =
      type === "audio" ? undefined : URL.createObjectURL(file);

    dispatch(
      integrationActions.metaAppendLocalMessage({
        _id: localId,
        conversationId: activeChatId,
        integrationId: whatsAppMetaIntegration!._id,
        ownerId: userId,
        direction: WAMetaMessageDirection.OUTGOING,
        from: whatsAppMetaIntegration!.config.phoneNumberId,
        sentBy: {
          _id: userId,
          name: loggedInUser?.name,
          picture: loggedInUser?.picture,
        },
        type,
        status: "queued",
        timestamp: String(Math.floor(Date.now() / 1000)),
        localPreviewUrl,
        pending: true,
        [type]: {
          ...(type === "document" ? { filename: file.name } : {}),
          ...(caption ? { caption } : {}),
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      } as unknown as ConversationMessage),
    );

    if (caption) {
      setMessage("");
    }

    setPendingFile(null);
    onCancelReply?.();
    requestAnimationFrame(whatsappChatScrollToBottom);

    try {
      setIsUploading(true);

      await dispatch(
        integrationActions.sendWhatsappMetaMedia({
          userId,
          integrationId: whatsAppMetaIntegration!._id,
          conversationId: activeChatId,
          file,
          caption,
          voice,
          clientId: localId,
          contextMessageId: replyTo?.metaMessageId,
        }),
      ).unwrap();
    } catch (error) {
      console.log(error);
      AtiraToast.apiError(error);

      dispatch(
        integrationActions.metaRemoveMessage({
          _id: localId,
          conversationId: activeChatId,
        }),
      );

      if (caption) {
        setMessage(caption);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const onFileChosen = (file: File) => {
    if (mediaTypeOf(file.type) === "image") {
      sendAttachment(file);
    } else {
      setPendingFile(file);
    }

    return false;
  };

  const onTyping = () => {
    if (
      !activeChatId ||
      Date.now() - typingSentAtRef.current < TYPING_HOLD_MS
    ) {
      return;
    }

    typingSentAtRef.current = Date.now();

    dispatch(
      integrationActions.showWhatsappMetaTyping({
        userId,
        integrationId: whatsAppMetaIntegration!._id,
        conversationId: activeChatId,
      }),
    );
  };

  const onSendTemplate = async (
    template: MetaMessageTemplate,
    templateParameters?: MetaTemplateParameters,
  ) => {
    if (!activeChatId) {
      return;
    }

    try {
      setIsSending(true);

      await dispatch(
        integrationActions.sendWhatsAppMetaMessage({
          userId,
          conversationId: activeChatId,
          integrationId: whatsAppMetaIntegration!._id,
          to: chat!.waId,
          type: "template",
          template,
          templateParameters,
        }),
      ).unwrap();

      setTemplatesOpen(false);
      requestAnimationFrame(() => whatsappChatScrollToBottom());
    } catch (error) {
      console.log(error);
      AtiraToast.apiError(error);
    } finally {
      setIsSending(false);
    }
  };

  const onSendMessage = async () => {
    if (!activeChatId) {
      return;
    }

    try {
      setIsSending(true);

      await dispatch(
        integrationActions.sendWhatsAppMetaMessage({
          userId,
          conversationId: activeChatId,
          integrationId: whatsAppMetaIntegration!._id,
          to: chat!.waId,
          type: "text",
          text: { body: message },
          ...(replyTo?.metaMessageId
            ? { context: { message_id: replyTo.metaMessageId } }
            : {}),
        }),
      ).unwrap();

      setMessage("");
      delete draftsRef.current[activeChatId];
      onCancelReply?.();
      // Sending dismisses the indicator on their side, so the next keystroke
      // should be free to raise it again
      typingSentAtRef.current = 0;
      requestAnimationFrame(() => {
        whatsappChatScrollToBottom();
        inputRef.current?.focus();
      });
    } catch (error) {
      console.log(error);
      AtiraToast.apiError(error);
    } finally {
      setIsSending(false);
    }
  };

  const voice = useVoiceRecorder({
    onFinish: (file) => sendAttachment(file, true),
    onError: (error) => {
      console.log(error);
      AtiraToast.error(t("integration.whatsapp.meta.voice.failed"));
    },
  });

  useEffect(() => {
    return () => {
      voice.cancel();
    };
  }, [activeChatId, voice.cancel]);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (activeChatId) {
        inputRef.current?.focus();
      }
    });
  }, [activeChatId]);

  useEffect(() => {
    const leaving = previousChatRef.current;

    if (leaving && leaving !== activeChatId) {
      draftsRef.current[leaving] = message;
    }

    if (leaving !== activeChatId) {
      previousChatRef.current = activeChatId;
      setMessage(draftsRef.current[activeChatId] || "");
    }
    // `message` is read, not watched: this runs when the chat changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChatId]);

  if (!activeChatId || !whatsAppMetaIntegration || !chat) {
    return null;
  }

  if (voice.recording) {
    return (
      <ChatBox>
        <StyledClickable
          onClick={voice.cancel}
          title={t("integration.whatsapp.meta.voice.cancel")}
        >
          <AtiraIcon icon={faTrash} size="lg" color="danger" />
        </StyledClickable>

        <RecordingBar
          role="group"
          aria-label={t("integration.whatsapp.meta.voice.recording")}
        >
          <RecordingDot />

          <WAVoiceLevelBars analyser={voice.analyser} />

          <Text fontSize="s" color="darkerSub">
            {formatDuration(voice.seconds)}
          </Text>
        </RecordingBar>

        <SendButton
          onClick={voice.send}
          title={t("floatingMenu.leaveMessage.send")}
        >
          <AtiraIcon icon={faPaperPlane} size="lg" color="white" />
        </SendButton>
      </ChatBox>
    );
  }

  return (
    <Fragment>
      {replyTo ? (
        <ReplyBar>
          <ReplyQuote>
            <Text fontSize="xs" color="main">
              {quote.author}
            </Text>
            <ReplySnippet fontSize="s">{quote.preview}</ReplySnippet>
          </ReplyQuote>

          <StyledClickable
            onClick={onCancelReply}
            title={t("integration.whatsapp.meta.reply.cancel")}
          >
            <AtiraIcon icon={faXmark} size="lg" color="main" />
          </StyledClickable>
        </ReplyBar>
      ) : null}

      <ChatBox>
        <Input
          className="atira-wa-composer-input"
          dir="auto"
          placeholder={"..."}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            onTyping();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              onSendMessage();
            }
          }}
          disabled={isSending}
          ref={inputRef}
        />
        <Tooltip title={t("integration.whatsapp.meta.chatbox.send")}>
          <SendButton
            onClick={() => onSendMessage()}
            disabled={isSending || !message.trim()}
          >
            <AtiraIcon icon={faPaperPlane} size="lg" color="white" />
          </SendButton>
        </Tooltip>

        <ImgCrop
          quality={1}
          aspectSlider
          showReset
          beforeCrop={(file) => mediaTypeOf(file.type) === "image"}
          modalTitle={t("integration.whatsapp.meta.media.cropTitle")}
          modalOk={t("integration.whatsapp.meta.media.send")}
          resetText={t("common.reset")}
        >
          <AttachUpload
            // Read off this child by antd-img-crop, which otherwise sets
            // accept="image/*" itself - the picker then refuses to offer
            // documents, video or audio at all
            accept={ATTACHABLE_TYPES}
            showUploadList={false}
            maxCount={1}
            beforeUpload={onFileChosen as any}
            disabled={isUploading || isSending}
            data-testid={TestIDs.integration.whatsappMeta.chat.attach}
          >
            <Tooltip title={t("integration.whatsapp.meta.media.attach")}>
              <StyledClickable disabled={isUploading || isSending}>
                {isUploading ? (
                  <Spinner color="main" />
                ) : (
                  <AtiraIcon icon={faPaperclip} size="lg" color="main" />
                )}
              </StyledClickable>
            </Tooltip>
          </AttachUpload>
        </ImgCrop>

        {voice.supported ? (
          <Tooltip title={t("integration.whatsapp.meta.voice.record")}>
            <StyledClickable
              onClick={voice.start}
              disabled={isUploading || isSending}
              dataTestId={TestIDs.integration.whatsappMeta.chat.record}
            >
              <AtiraIcon icon={faMicrophone} size="lg" color="main" />
            </StyledClickable>
          </Tooltip>
        ) : null}

        <Tooltip title={t("integration.whatsapp.meta.chatbox.templates")}>
          <StyledClickable onClick={() => setTemplatesOpen(true)}>
            <AtiraIcon icon={faClipboard} size="lg" color="main" />
          </StyledClickable>
        </Tooltip>
      </ChatBox>

      <WATemplateSendModal
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        templates={messagesTemplates || []}
        sending={isSending}
        onSend={onSendTemplate}
      />

      <WarningModal
        isOpen={Boolean(pendingFile)}
        onClose={() => setPendingFile(null)}
        onConfirm={() => pendingFile && sendAttachment(pendingFile)}
        loading={isUploading}
        title={t("integration.whatsapp.meta.media.confirmTitle")}
        description={t("integration.whatsapp.meta.media.confirmDescription", {
          filename: pendingFile?.name,
        })}
        confirmButtonText={t("integration.whatsapp.meta.media.send")}
      />
    </Fragment>
  );
};
